import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  User,
  FileText,
  Download,
  CreditCard,
  CalendarClock,
  AlertCircle,
  CheckCircle,
  GraduationCap,
  Target,
  Newspaper,
  Filter,
  X,
  ExternalLink,
  ChevronDown,
  Check,
  Copy
} from 'lucide-react';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import clsx from 'clsx';
import { SidebarLayout } from '../components/layout/SidebarLayout';
import { TWContainer } from '../components/ui/TWContainer';
import { useAuth } from '../contexts/AuthContext';
import { useLenis } from '../hooks/useLenis';
import { apiClient, getApiBaseUrl } from '../services/api/apiClient';
import { ProfileEditForm } from '../components/profile/ProfileEditForm';
import PrioritySupportNotificationSettings from '../components/profile/PrioritySupportNotificationSettings';
import { SubscriptionCards } from '../components/profile/SubscriptionCards';
import { formatCurrency } from '../utils/formatUtils';
import { TWCheckbox } from '../components/ui/TWCheckbox';
import { twToast } from '../components/ui/TWToast';
import {
  TWTable,
  TWTableHead,
  TWTableBody,
  TWTableRow,
  TWTableHeader,
  TWTableCell
} from '../components/ui/TWTable';
import { TWPagination } from '../components/ui/TWPagination';
import {
  getAvailablePlans,
  getSubscriptionStatus,
  cancelSubscription,
  reactivateSubscription,
  cancelPendingDowngrade,
  getCustomerPortalUrl,
  getActiveDiscount,
  SubscriptionPlan,
  SubscriptionStatusResponse,
  ActiveDiscount
} from '../services/payment/subscriptionService';
import { PlanChangeDialog } from '../components/profile/PlanChangeDialog';
import { DowngradeResourceSelector } from '../components/profile/DowngradeResourceSelector';
import { BlogDowngradeSelector } from '../components/profile/BlogDowngradeSelector';
import fcmsLimitsService, { type FcmsFeatureLimits } from '../services/fcms/fcmsLimitsService';
import { TWPageHeaderTabs } from '../components/ui/TWPageHeader';
import { invalidateFeatureAccessCache } from '../hooks/useFeatureAccess';
import { TWBadge } from '../components/ui/TWBadge';
import { UserAvatar } from '../components/ui/UserAvatar';

type ProfileTab = 'profile' | 'subscription' | 'invoices';

const tabFromUrl: Record<string, ProfileTab> = {
  'profil': 'profile',
  'abonnement': 'subscription',
  'rechnungen': 'invoices',
};

export const ProfilePage: React.FC = () => {
  useLenis();

  const navigate = useNavigate();
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const { user, refreshUserProfile, refreshSubscriptions } = useAuth();

  const getInitialTab = (): ProfileTab => {
    if (urlTab && tabFromUrl[urlTab]) {
      return tabFromUrl[urlTab];
    }
    return 'profile';
  };

  const [activeTab, setActiveTab] = useState<ProfileTab>(getInitialTab);

  // Subscription state
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusResponse | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [activeDiscountData, setActiveDiscountData] = useState<ActiveDiscount | null>(null);
  const [fcmsLimits, setFcmsLimits] = useState<FcmsFeatureLimits | null>(null);
  const [cancellingSubscription, setCancellingSubscription] = useState<string | null>(null);
  const [reactivatingSubscription, setReactivatingSubscription] = useState<string | null>(null);
  const [cancelSuccessInfo, setCancelSuccessInfo] = useState<{
    planName: string;
    accessUntil: string;
  } | null>(null);

  // Invoice state
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(true);
  const [invoiceFilter, setInvoiceFilter] = useState<'all' | 'T' | 'A' | 'B' | 'F'>('all');
  const [invoiceSortKey, setInvoiceSortKey] = useState<'invoice_number' | 'issue_date' | 'total' | 'payment_status'>('issue_date');
  const [invoiceSortDir, setInvoiceSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(new Set());
  const [previewInvoice, setPreviewInvoice] = useState<any>(null);
  const [downloadingZip, setDownloadingZip] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Plan Change Dialog state
  const [planChangeDialogOpen, setPlanChangeDialogOpen] = useState(false);
  const [planChangeTargetSlug, setPlanChangeTargetSlug] = useState<string>('');

  // Downgrade Resource Selector state
  // family entscheidet, welcher Selector geöffnet wird:
  //   'fcms'    -> DowngradeResourceSelector (Assets/Watchlists/YT/Charts)
  //   'bitcoin' -> BlogDowngradeSelector (nur YT-Kanäle)
  const [resourceSelectorOpen, setResourceSelectorOpen] = useState(false);
  const [resourceSelectorFamily, setResourceSelectorFamily] = useState<'fcms' | 'bitcoin'>('fcms');
  const [downgradeEffectiveDate, setDowngradeEffectiveDate] = useState<string>('');

  useEffect(() => {
    refreshUserProfile();
    fetchSubscriptionData();
    fetchInvoices();
  }, []);

  useEffect(() => {
    const targetTab = urlTab && tabFromUrl[urlTab] ? tabFromUrl[urlTab] : 'profile';
    if (targetTab !== activeTab) {
      setActiveTab(targetTab);
    }
  }, [urlTab]);

  const fetchSubscriptionData = async () => {
    setLoadingSubscription(true);
    try {
      const [plansData, statusData, discountData] = await Promise.all([
        getAvailablePlans(),
        getSubscriptionStatus(),
        getActiveDiscount()
      ]);
      setPlans(plansData);
      setSubscriptionStatus(statusData);
      setActiveDiscountData(discountData);

      // FCMS-Limits laden (Backend entscheidet anhand Abo/Rolle)
      try {
        const limits = await fcmsLimitsService.getLimits();
        setFcmsLimits(limits);
      } catch {
        setFcmsLimits(null);
      }
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleSubscribe = (planSlug: string, interval: 'monthly' | 'yearly' = 'monthly') => {
    const params = new URLSearchParams({ plan: planSlug });
    if (interval === 'yearly') params.set('interval', 'yearly');
    navigate(`/checkout?${params.toString()}`);
  };

  const handlePlanChange = (targetPlanSlug: string) => {
    setPlanChangeTargetSlug(targetPlanSlug);
    setPlanChangeDialogOpen(true);
  };

  const handlePlanChangeSuccess = () => {
    setPlanChangeDialogOpen(false);
    invalidateFeatureAccessCache();
    fetchSubscriptionData();
  };

  /**
   * Wird vom PlanChangeDialog aufgerufen, nachdem ein Downgrade mit
   * Limit-Überschreitung ausgeführt wurde. Wählt den richtigen Selector
   * basierend auf der Plan-Familie der aktuell aktiven Subscription.
   */
  const handleDowngradeNeedsSelection = (effectiveDate: string) => {
    setPlanChangeDialogOpen(false);
    setDowngradeEffectiveDate(effectiveDate);

    // Familie aus der aktiven Sub ableiten (planChangeTargetSlug ist der ZIEL-Plan,
    // wir wollen aber die Familie des aktuellen Plans -- die haben aber dieselbe Familie,
    // weil Cross-Family-Wechsel gesperrt ist).
    const family: 'fcms' | 'bitcoin' = planChangeTargetSlug?.startsWith('bitcoin-') ? 'bitcoin' : 'fcms';
    setResourceSelectorFamily(family);
    setResourceSelectorOpen(true);
  };

  const handleResourceSelectionSaved = () => {
    setResourceSelectorOpen(false);
    invalidateFeatureAccessCache();
    fetchSubscriptionData();
  };

  /**
   * "Ressourcen-Auswahl bearbeiten"-Button auf der Plan-Karte.
   * Findet die Sub mit pending Downgrade (FCMS oder Bitcoin) und öffnet den passenden Selector.
   */
  const handleEditResourceSelection = () => {
    const activeSub = subscriptionStatus?.subscriptions.find(s =>
      s.pendingPlanChangeAt && (
        s.planSlug?.startsWith('fcms-') || s.planSlug?.startsWith('bitcoin-')
      )
    );
    if (!activeSub?.pendingPlanChangeAt) return;

    setDowngradeEffectiveDate(activeSub.pendingPlanChangeAt);
    const family: 'fcms' | 'bitcoin' = activeSub.planSlug?.startsWith('bitcoin-') ? 'bitcoin' : 'fcms';
    setResourceSelectorFamily(family);
    setResourceSelectorOpen(true);
  };

  const handleCancelPendingDowngrade = async () => {
    try {
      await cancelPendingDowngrade();
      invalidateFeatureAccessCache();
      fetchSubscriptionData();
    } catch (error: any) {
      console.error('Failed to cancel pending downgrade:', error);
      alert(error.message || 'Fehler beim Stornieren des Plan-Wechsels');
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    const subToCancel = subscriptionStatus?.subscriptions.find(s => s.id === subscriptionId);
    const planName = subToCancel?.planName || 'Abonnement';
    const accessUntil = subToCancel?.currentPeriodEnd
      ? new Date(subToCancel.currentPeriodEnd).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: 'long',
          year: 'numeric'
        })
      : null;

    setCancellingSubscription(subscriptionId);
    try {
      await cancelSubscription(subscriptionId);
      await fetchSubscriptionData();
      if (accessUntil) {
        setCancelSuccessInfo({ planName, accessUntil });
      }
    } catch (error: any) {
      console.error('Failed to cancel subscription:', error);
      alert(error.message || 'Fehler beim Kündigen des Abonnements');
    } finally {
      setCancellingSubscription(null);
    }
  };

  const handleReactivateSubscription = async (subscriptionId: string) => {
    setReactivatingSubscription(subscriptionId);
    try {
      await reactivateSubscription(subscriptionId);
      await fetchSubscriptionData();
      twToast.success('Abonnement reaktiviert -- läuft normal weiter.');
    } catch (error: any) {
      console.error('Failed to reactivate subscription:', error);
      twToast.error(error.message || 'Fehler beim Reaktivieren des Abonnements');
    } finally {
      setReactivatingSubscription(null);
    }
  };

  const fetchInvoices = async () => {
    try {
      setLoadingInvoices(true);
      const response = await apiClient.get('/invoices/my');
      setInvoices(response.data.invoices || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const getInvoicePdfUrl = (pdfUrl: string, forDownload = false): string | null => {
    if (!pdfUrl || !pdfUrl.startsWith('/invoices/')) {
      console.warn('Ungültige pdf_url:', pdfUrl);
      return null;
    }
    // Verwende die zentrale API-Base-URL-Funktion für konsistente URL-Generierung
    // Dies unterstützt localhost, lokales Netzwerk (192.168.x.x) und Production
    const baseUrl = getApiBaseUrl();
    const dynamicPath = pdfUrl.startsWith('/invoices/file/') ? pdfUrl : pdfUrl.replace(/^\/invoices\//, '/invoices/file/');
    const url = `${baseUrl}${dynamicPath}`;
    return forDownload ? `${url}?download=true` : url;
  };

  const handleDownloadInvoice = (invoice: any) => {
    const downloadUrl = getInvoicePdfUrl(invoice.pdf_url, true);
    if (downloadUrl) {
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const formatInvoiceDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getInvoiceCategory = (invoiceNumber: string): 'T' | 'A' | 'B' | 'F' | null => {
    const match = invoiceNumber.match(/^RF-([TABF])-/);
    return match ? match[1] as 'T' | 'A' | 'B' | 'F' : null;
  };

  const getCategoryInfo = (category: 'T' | 'A' | 'B' | 'F' | null) => {
    switch (category) {
      case 'T': return { label: 'Termine', icon: CalendarClock, color: 'gold' };
      case 'A': return { label: 'Academy', icon: GraduationCap, color: 'blue' };
      case 'B': return { label: 'Blog', icon: Newspaper, color: 'green' };
      case 'F': return { label: 'FCMS', icon: Target, color: 'gold' };
      default: return { label: 'Sonstige', icon: FileText, color: 'gray' };
    }
  };

  const getStatusInfo = (paymentStatus: string) => {
    switch (paymentStatus) {
      case 'paid': return { label: 'Bezahlt', color: 'green' };
      case 'pending': return { label: 'Nicht bezahlt', color: 'red' };
      case 'partially_paid': return { label: 'Teilweise bezahlt', color: 'yellow' };
      case 'failed': return { label: 'Fehlgeschlagen', color: 'red' };
      case 'refunded': return { label: 'Erstattet', color: 'yellow' };
      default: return { label: paymentStatus, color: 'gray' };
    }
  };

  const filteredInvoices = invoices.filter(invoice => {
    if (invoiceFilter === 'all') return true;
    return getInvoiceCategory(invoice.invoice_number) === invoiceFilter;
  });

  // Sortierung (desc = neueste/grösste zuerst als Default)
  const sortedInvoices = React.useMemo(() => {
    const arr = [...filteredInvoices];
    const dir = invoiceSortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      switch (invoiceSortKey) {
        case 'invoice_number':
          return a.invoice_number.localeCompare(b.invoice_number, 'de') * dir;
        case 'total':
          return (Number(a.total) - Number(b.total)) * dir;
        case 'payment_status':
          return String(a.payment_status || '').localeCompare(String(b.payment_status || ''), 'de') * dir;
        case 'issue_date':
        default:
          return (new Date(a.issue_date).getTime() - new Date(b.issue_date).getTime()) * dir;
      }
    });
    return arr;
  }, [filteredInvoices, invoiceSortKey, invoiceSortDir]);

  // Pagination-Berechnung
  const totalPages = Math.ceil(sortedInvoices.length / itemsPerPage);
  const paginatedInvoices = sortedInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleInvoiceSort = (key: typeof invoiceSortKey) => {
    if (invoiceSortKey === key) {
      setInvoiceSortDir(invoiceSortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setInvoiceSortKey(key);
      setInvoiceSortDir('desc');
    }
  };

  const toggleInvoiceSelection = (invoiceId: string) => {
    setSelectedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  const toggleAllInvoices = () => {
    if (selectedInvoices.size === filteredInvoices.length) {
      setSelectedInvoices(new Set());
    } else {
      setSelectedInvoices(new Set(filteredInvoices.map(inv => inv.id)));
    }
  };

  const ZIP_THRESHOLD = 3;
  const MAX_PER_ZIP = 100;

  const downloadInvoicesIndividually = (invoicesList: any[]) => {
    invoicesList.forEach((invoice, index) => {
      setTimeout(() => {
        handleDownloadInvoice(invoice);
      }, index * 100);
    });
  };

  const downloadInvoicesAsZip = async (invoiceIds: string[], partNumber?: number) => {
    if (invoiceIds.length === 0) return;
    setDownloadingZip(true);
    try {
      const response = await apiClient.post('/invoices/download-zip', { invoiceIds }, {
        responseType: 'blob'
      });
      if (response.data instanceof Blob && response.data.type === 'application/zip') {
        const url = window.URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url;
        const contentDisposition = response.headers['content-disposition'];
        const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
        let filename = filenameMatch ? filenameMatch[1] : `Rechnungen_RENDITEFUCHS_${new Date().toISOString().split('T')[0]}.zip`;
        if (partNumber !== undefined) {
          filename = filename.replace('.zip', `_Teil${partNumber}.zip`);
        }
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('ZIP-Download fehlgeschlagen:', error);
    } finally {
      setDownloadingZip(false);
    }
  };

  const downloadInvoices = async (invoiceIds: string[]) => {
    if (invoiceIds.length === 0) return;
    if (invoiceIds.length <= ZIP_THRESHOLD) {
      const invoicesToDownload = invoices.filter(inv => invoiceIds.includes(inv.id));
      downloadInvoicesIndividually(invoicesToDownload);
      return;
    }
    if (invoiceIds.length > MAX_PER_ZIP) {
      setDownloadingZip(true);
      const chunks: string[][] = [];
      for (let i = 0; i < invoiceIds.length; i += MAX_PER_ZIP) {
        chunks.push(invoiceIds.slice(i, i + MAX_PER_ZIP));
      }
      for (let i = 0; i < chunks.length; i++) {
        const chunk = chunks[i];
        if (chunk) {
          await downloadInvoicesAsZip(chunk, i + 1);
          if (i < chunks.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      setDownloadingZip(false);
      return;
    }
    await downloadInvoicesAsZip(invoiceIds);
  };

  const downloadSelectedInvoices = () => {
    downloadInvoices(Array.from(selectedInvoices));
  };

  const downloadAllInvoices = () => {
    downloadInvoices(filteredInvoices.map(inv => inv.id));
  };

  const handleTabChange = (tabId: string) => {
    const tab = tabId as ProfileTab;
    setActiveTab(tab);
    // URL-Mapping: profile -> profil, subscription -> abonnement, invoices -> rechnungen
    const urlMapping: Record<ProfileTab, string> = {
      'profile': 'profil',
      'subscription': 'abonnement',
      'invoices': 'rechnungen',
    };
    const newPath = `/profil/${urlMapping[tab]}`;
    navigate(newPath, { replace: true });
  };

  // Tabs für PageHeader
  const pageHeaderTabs = [
    { id: 'profile', name: 'Profil', icon: User, current: activeTab === 'profile' },
    { id: 'subscription', name: 'Abonnement', icon: CreditCard, current: activeTab === 'subscription' },
    { id: 'invoices', name: 'Rechnungen', icon: FileText, current: activeTab === 'invoices' },
  ];

  // ============================================
  // RENDER: Not authenticated
  // ============================================
  if (!user) {
    return (
      <SidebarLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-gray-400 dark:text-gray-500 mb-4">
              Du musst angemeldet sein, um dein Profil zu sehen.
            </p>
            <Link to="/login">
              <button className="px-6 py-3 bg-brand hover:bg-brand-hover text-gray-900 font-medium rounded-lg transition-colors">
                Jetzt anmelden
              </button>
            </Link>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  const invoiceFilterButtons: { id: 'all' | 'T' | 'A' | 'B' | 'F'; label: string; icon: any }[] = [
    { id: 'all', label: 'Alle', icon: Filter },
    { id: 'T', label: 'Termine', icon: CalendarClock },
    { id: 'A', label: 'Academy', icon: GraduationCap },
    { id: 'B', label: 'Blog', icon: Newspaper },
    { id: 'F', label: 'FCMS', icon: Target },
  ];

  // ============================================
  // MAIN RENDER
  // ============================================
  return (
    <SidebarLayout showFooter>
      {/* Header - IMMER volle Breite */}
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <TWPageHeaderTabs
          title="Mein Profil"
          tabs={pageHeaderTabs}
          onTabChange={handleTabChange}
          accentColor="gold"
        />
      </div>

      {/* Content - schmaler Formular-Container (max-w-5xl = 1024px) für bessere Lesbarkeit */}
      <TWContainer size="default" spacing="default">
        {/* Tab Content */}
        <div>
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <>
              {/* Account Info - nur im Profil-Tab */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-x-8 gap-y-6 pb-8 mb-6 border-b border-gray-200 dark:border-white/10">
                {/* Links: Avatar + Name */}
                <div className="flex items-center gap-4" data-sensitive>
                  <UserAvatar user={user} size="lg" />
                  <div className="min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {user.firstName} {user.lastName}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm truncate">{user.email}</p>
                  </div>
                </div>
                {/* Rechts: Meta-Informationen */}
                <div className="xl:col-span-2 grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-6" data-sensitive>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Kontotyp</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                      {user.accountType === 'company' ? 'Unternehmen' : 'Privat'}
                    </p>
                  </div>
                  {user.accountType === 'company' && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Firma</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">{user.companyName || 'Nicht angegeben'}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Mitglied seit</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                      {new Date(user.createdAt).toLocaleDateString('de-DE')}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Rolle</p>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mt-1">
                      {user.role === 'admin' ? 'Administrator' : user.isBetaTester ? 'Beta-Tester' : 'Kunde'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Benutzer-ID</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <code className="text-xs font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
                        {user.id.substring(0, 8)}
                      </code>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(user.id);
                          twToast.success('Benutzer-ID kopiert');
                        }}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                        title="Vollständige ID kopieren"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <ProfileEditForm user={user} onUpdate={refreshUserProfile} />

              {/* Priority-Support-Benachrichtigungen — nur sichtbar bei Chat-Zugang */}
              <PrioritySupportNotificationSettings />
            </>
          )}

          {/* Subscription Tab */}
          {activeTab === 'subscription' && (
            <div>
              {loadingSubscription ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <>
                  <SubscriptionCards
                    plans={plans}
                    subscriptions={subscriptionStatus?.subscriptions || []}
                    fcmsLimits={fcmsLimits}
                    activeDiscount={activeDiscountData}
                    onSubscribe={handleSubscribe}
                    onCancelSubscription={handleCancelSubscription}
                    onReactivateSubscription={handleReactivateSubscription}
                    onPlanChange={handlePlanChange}
                    onCancelPendingDowngrade={handleCancelPendingDowngrade}
                    onEditResourceSelection={handleEditResourceSelection}
                    cancellingSubscription={cancellingSubscription}
                    reactivatingSubscription={reactivatingSubscription}
                  />
                  {/* Stripe Customer Portal - nur bei aktivem Stripe-Abo */}
                  {subscriptionStatus?.subscriptions?.some(
                    (s: any) => s.paymentProvider === 'stripe' && s.status === 'active'
                  ) && (
                    <div className="mt-6 flex justify-center">
                      <button
                        onClick={async () => {
                          try {
                            const portalUrl = await getCustomerPortalUrl(window.location.href);
                            window.location.href = portalUrl;
                          } catch (err: any) {
                            console.error('Portal error:', err);
                          }
                        }}
                        className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                      >
                        <CreditCard className="w-4 h-4" />
                        Zahlungsmethode verwalten
                        <ExternalLink className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <div>
              {loadingInvoices ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-8 h-8 border-2 border-brand border-t-transparent rounded-full animate-spin" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 dark:text-gray-500 mb-4" />
                  <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">Keine Rechnungen</p>
                  <p className="text-gray-600 dark:text-gray-400">
                    Hier erscheinen deine Rechnungen, sobald du Zahlungen tätigst.
                  </p>
                </div>
              ) : (
                <>
                  {/* Filter Bar */}
                  <div className="flex items-center justify-between gap-3 mb-4">
                    {/* Mobile: Dropdown */}
                    <Menu as="div" className="relative sm:hidden">
                      <MenuButton className="inline-flex items-center gap-2 rounded-lg bg-white dark:bg-white/5 px-3 py-2 text-sm font-medium text-gray-900 dark:text-white ring-1 ring-gray-200 dark:ring-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors">
                        {(() => {
                          const current = invoiceFilterButtons.find(f => f.id === invoiceFilter);
                          const CurrentIcon = current?.icon || Filter;
                          const count = invoiceFilter === 'all'
                            ? invoices.length
                            : invoices.filter(inv => getInvoiceCategory(inv.invoice_number) === invoiceFilter).length;
                          return (
                            <>
                              <CurrentIcon className="w-4 h-4 text-brand" />
                              <span>{current?.label || 'Alle'}</span>
                              <span className="px-1.5 py-0.5 rounded text-xs bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-gray-400">
                                {count}
                              </span>
                            </>
                          );
                        })()}
                        <ChevronDown className="w-4 h-4 text-gray-400 -mr-0.5" />
                      </MenuButton>
                      <MenuItems
                        transition
                        anchor="bottom start"
                        className="glass-surface min-w-[13rem] origin-top-left rounded-2xl p-1 text-sm transition duration-100 ease-out [--anchor-gap:--spacing(2)] focus:outline-none data-closed:scale-95 data-closed:opacity-0 z-[10000]"
                      >
                        {invoiceFilterButtons.map((filter) => {
                          const count = filter.id === 'all'
                            ? invoices.length
                            : invoices.filter(inv => getInvoiceCategory(inv.invoice_number) === filter.id).length;
                          const FilterIcon = filter.icon;
                          const isActive = invoiceFilter === filter.id;
                          return (
                            <MenuItem key={filter.id}>
                              <button
                                type="button"
                                onClick={() => {
                                  setInvoiceFilter(filter.id);
                                  setSelectedInvoices(new Set());
                                  setCurrentPage(1);
                                }}
                                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 data-focus:bg-gray-100 dark:data-focus:bg-white/10 ${isActive ? 'text-brand font-medium' : 'text-gray-700 dark:text-gray-200'}`}
                              >
                                <FilterIcon className={`w-4 h-4 ${isActive ? 'text-brand' : 'text-gray-700 dark:text-gray-300'}`} />
                                <span className="flex-1 text-left">{filter.label}</span>
                                <span className={`px-1.5 py-0.5 rounded text-xs ${isActive ? 'bg-brand text-gray-950 font-medium' : 'bg-gray-200/60 dark:bg-white/10 text-gray-600 dark:text-gray-400'}`}>
                                  {count}
                                </span>
                                {isActive && <Check className="w-4 h-4 text-brand" />}
                              </button>
                            </MenuItem>
                          );
                        })}
                      </MenuItems>
                    </Menu>

                    {/* Desktop/Tablet: Pills (Tailwind-Plus Pattern 05 - Tabs in pills with brand color) */}
                    <nav aria-label="Rechnungs-Filter" className="hidden sm:flex sm:flex-wrap gap-2">
                      {invoiceFilterButtons.map((filter) => {
                        const count = filter.id === 'all'
                          ? invoices.length
                          : invoices.filter(inv => getInvoiceCategory(inv.invoice_number) === filter.id).length;
                        const FilterIcon = filter.icon;
                        const isActive = invoiceFilter === filter.id;
                        return (
                          <button
                            key={filter.id}
                            type="button"
                            aria-current={isActive ? 'page' : undefined}
                            onClick={() => {
                              setInvoiceFilter(filter.id);
                              setSelectedInvoices(new Set());
                              setCurrentPage(1);
                            }}
                            className={clsx(
                              'inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                              isActive
                                ? 'bg-brand text-gray-900 dark:bg-brand/20 dark:text-brand'
                                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                            )}
                          >
                            <FilterIcon className="w-4 h-4" />
                            <span>{filter.label}</span>
                            <span
                              className={clsx(
                                'ml-1 rounded-full px-2 py-0.5 text-xs font-medium',
                                isActive
                                  ? 'bg-gray-900/15 text-gray-900 dark:bg-brand dark:text-gray-900'
                                  : 'bg-gray-100 text-gray-900 dark:bg-white/10 dark:text-gray-300'
                              )}
                            >
                              {count}
                            </span>
                          </button>
                        );
                      })}
                    </nav>

                    <button
                      onClick={downloadAllInvoices}
                      disabled={downloadingZip}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg text-sm font-medium
                        bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300
                        border border-gray-200 dark:border-white/10
                        hover:bg-gray-200 dark:hover:bg-white/10
                        disabled:opacity-50 transition-colors shrink-0"
                    >
                      {downloadingZip ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      <span className="hidden sm:inline">{downloadingZip ? 'Wird erstellt...' : 'Alle herunterladen'}</span>
                    </button>
                  </div>

                  {/* Selection Bar */}
                  {selectedInvoices.size > 0 && (
                    <div className="flex items-center justify-between p-3 mb-4 rounded-lg bg-brand/10 border border-brand/20">
                      <span className="text-sm text-brand">
                        {selectedInvoices.size} von {filteredInvoices.length} ausgewählt
                      </span>
                      <button
                        onClick={downloadSelectedInvoices}
                        disabled={downloadingZip}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium
                          bg-brand text-gray-900 hover:bg-brand-hover disabled:opacity-50 transition-colors"
                      >
                        {downloadingZip ? (
                          <div className="w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Download className="w-4 h-4" />
                        )}
                        <span>Ausgewählte herunterladen</span>
                      </button>
                    </div>
                  )}

                  {/* Mobile: Card-Ansicht */}
                  <div className="sm:hidden space-y-3">
                    {/* Alle auswählen */}
                    <div className="flex items-center gap-2 px-1">
                      <TWCheckbox
                        checked={selectedInvoices.size === filteredInvoices.length && filteredInvoices.length > 0}
                        onChange={toggleAllInvoices}
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Alle auswählen
                      </span>
                    </div>

                    {paginatedInvoices.map((invoice) => {
                      const category = getInvoiceCategory(invoice.invoice_number);
                      const categoryInfo = getCategoryInfo(category);
                      const CategoryIcon = categoryInfo.icon;
                      const statusInfo = getStatusInfo(invoice.payment_status);
                      const isSelected = selectedInvoices.has(invoice.id);

                      return (
                        <div
                          key={invoice.id}
                          onClick={() => setPreviewInvoice(invoice)}
                          className={`
                            rounded-xl border bg-white dark:bg-gray-900 p-4 cursor-pointer
                            transition-colors active:bg-gray-50 dark:active:bg-white/5
                            ${isSelected
                              ? 'border-brand/50 bg-brand/5'
                              : 'border-gray-200 dark:border-white/10'
                            }
                          `}
                        >
                          {/* Header: Checkbox + Rechnungsnr + Betrag */}
                          <div className="flex items-start justify-between gap-3 mb-3">
                            <div className="flex items-center gap-3">
                              <div onClick={(e) => e.stopPropagation()}>
                                <TWCheckbox
                                  checked={isSelected}
                                  onChange={() => toggleInvoiceSelection(invoice.id)}
                                />
                              </div>
                              <div>
                                <p className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                                  {invoice.invoice_number}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                  {formatInvoiceDate(invoice.issue_date)}
                                </p>
                              </div>
                            </div>
                            <span className="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(invoice.total)}
                            </span>
                          </div>

                          {/* Footer: Badges + Download */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge color={categoryInfo.color} icon={<CategoryIcon className="w-3 h-3" />}>
                                {categoryInfo.label}
                              </Badge>
                              <Badge color={statusInfo.color}>
                                {statusInfo.label}
                              </Badge>
                            </div>
                            {invoice.pdf_url && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadInvoice(invoice);
                                }}
                                className="p-2 -m-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                                title="PDF herunterladen"
                              >
                                <Download className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Desktop/Tablet: Tabelle (Tailwind-Plus Card-Pattern) */}
                  <div className="hidden sm:block overflow-hidden rounded-lg shadow-sm outline-1 outline-black/5 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10 bg-white dark:bg-gray-900">
                    <TWTable hover hoverStyle="clickable">
                      <TWTableHead>
                        <TWTableRow disableHover>
                          <TWTableHeader className="w-12">
                            <TWCheckbox
                              checked={selectedInvoices.size === filteredInvoices.length && filteredInvoices.length > 0}
                              onChange={toggleAllInvoices}
                              title={selectedInvoices.size === filteredInvoices.length ? 'Alle abwählen' : 'Alle auswählen'}
                            />
                          </TWTableHeader>
                          <TWTableHeader
                            sortable
                            sortDirection={invoiceSortKey === 'invoice_number' ? invoiceSortDir : null}
                            onSort={() => toggleInvoiceSort('invoice_number')}
                          >
                            Rechnungsnr.
                          </TWTableHeader>
                          <TWTableHeader>Kategorie</TWTableHeader>
                          <TWTableHeader
                            sortable
                            sortDirection={invoiceSortKey === 'payment_status' ? invoiceSortDir : null}
                            onSort={() => toggleInvoiceSort('payment_status')}
                          >
                            Status
                          </TWTableHeader>
                          <TWTableHeader
                            sortable
                            sortDirection={invoiceSortKey === 'issue_date' ? invoiceSortDir : null}
                            onSort={() => toggleInvoiceSort('issue_date')}
                          >
                            Datum
                          </TWTableHeader>
                          <TWTableHeader
                            className="text-right"
                            sortable
                            sortDirection={invoiceSortKey === 'total' ? invoiceSortDir : null}
                            onSort={() => toggleInvoiceSort('total')}
                          >
                            Betrag
                          </TWTableHeader>
                          <TWTableHeader className="text-center">Download</TWTableHeader>
                        </TWTableRow>
                      </TWTableHead>
                      <TWTableBody>
                        {paginatedInvoices.map((invoice) => {
                          const category = getInvoiceCategory(invoice.invoice_number);
                          const categoryInfo = getCategoryInfo(category);
                          const CategoryIcon = categoryInfo.icon;
                          const statusInfo = getStatusInfo(invoice.payment_status);
                          const isSelected = selectedInvoices.has(invoice.id);

                          return (
                            <TWTableRow
                              key={invoice.id}
                              onClick={() => setPreviewInvoice(invoice)}
                              className={isSelected ? 'bg-brand/5' : ''}
                            >
                              <TWTableCell className="w-12" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                <TWCheckbox
                                  checked={isSelected}
                                  onChange={() => toggleInvoiceSelection(invoice.id)}
                                />
                              </TWTableCell>
                              <TWTableCell>
                                <span className="font-mono text-sm text-gray-900 dark:text-white">
                                  {invoice.invoice_number}
                                </span>
                              </TWTableCell>
                              <TWTableCell>
                                <Badge color={categoryInfo.color} icon={<CategoryIcon className="w-3 h-3" />}>
                                  {categoryInfo.label}
                                </Badge>
                              </TWTableCell>
                              <TWTableCell>
                                <Badge color={statusInfo.color}>
                                  {statusInfo.label}
                                </Badge>
                              </TWTableCell>
                              <TWTableCell className="text-sm text-gray-600 dark:text-gray-300">
                                {formatInvoiceDate(invoice.issue_date)}
                              </TWTableCell>
                              <TWTableCell className="text-right">
                                <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                                  {formatCurrency(invoice.total)}
                                </span>
                              </TWTableCell>
                              <TWTableCell className="text-center" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                {invoice.pdf_url && (
                                  <button
                                    onClick={() => handleDownloadInvoice(invoice)}
                                    className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                                    title="PDF herunterladen"
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                )}
                              </TWTableCell>
                            </TWTableRow>
                          );
                        })}
                      </TWTableBody>
                    </TWTable>
                  </div>

                  {/* Mobile: Einträge pro Seite + Pagination */}
                  {totalPages > 1 && (
                    <div className="mt-4">
                      {/* Mobile: Einträge pro Seite Dropdown */}
                      <div className="sm:hidden flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredInvoices.length)} von {filteredInvoices.length}
                        </span>
                        <Menu as="div" className="relative">
                          <MenuButton className="inline-flex items-center gap-1.5 rounded-lg bg-white dark:bg-white/5 px-2.5 py-1.5 text-sm text-gray-700 dark:text-gray-300 ring-1 ring-gray-200 dark:ring-white/10 hover:bg-gray-50 dark:hover:bg-white/10 transition-colors">
                            <span>{itemsPerPage} pro Seite</span>
                            <ChevronDown className="w-4 h-4 text-gray-400" />
                          </MenuButton>
                          <MenuItems
                            transition
                            anchor="bottom end"
                            className="z-50 mt-2 w-36 origin-top-right rounded-lg bg-white dark:bg-gray-800 ring-1 ring-gray-900/5 dark:ring-white/5 shadow-lg transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
                          >
                            <div className="py-1">
                              {[10, 25, 50].map((count) => (
                                <MenuItem key={count}>
                                  {({ focus }) => (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setItemsPerPage(count);
                                        setCurrentPage(1);
                                      }}
                                      className={`flex w-full items-center justify-between px-3 py-2 text-sm ${focus ? 'bg-gray-50 dark:bg-white/5' : ''} ${itemsPerPage === count ? 'text-brand font-medium' : 'text-gray-700 dark:text-gray-300'}`}
                                    >
                                      <span>{count} Einträge</span>
                                      {itemsPerPage === count && <Check className="w-4 h-4 text-brand" />}
                                    </button>
                                  )}
                                </MenuItem>
                              ))}
                            </div>
                          </MenuItems>
                        </Menu>
                      </div>

                      <TWPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        showInfo
                        totalItems={filteredInvoices.length}
                        itemsPerPage={itemsPerPage}
                        onItemsPerPageChange={setItemsPerPage}
                      />
                    </div>
                  )}

                  {filteredInvoices.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      Keine Rechnungen in dieser Kategorie.
                    </div>
                  )}
                </>
              )}

              {/* Invoice Preview Modal -- via Portal an document.body, damit es
                  über allen Layout-Stacking-Contexts (Footer etc.) liegt. */}
              {previewInvoice && createPortal(
                <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4" onClick={() => setPreviewInvoice(null)}>
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
                  <div
                    className="relative w-full max-w-4xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-xl shadow-2xl overflow-hidden flex flex-col"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Modal Header */}
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-white/10">
                      <div className="flex items-center gap-2">
                        <FileText className="w-5 h-5 text-brand" />
                        <h3 className="font-semibold text-gray-900 dark:text-white">{previewInvoice.invoice_number}</h3>
                      </div>
                      <button
                        onClick={() => setPreviewInvoice(null)}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {/* Modal Body */}
                    <div className="flex-1 overflow-hidden">
                      {(() => {
                        const pdfUrl = getInvoicePdfUrl(previewInvoice.pdf_url);
                        if (pdfUrl) {
                          return (
                            <>
                              {/* Mobile: Buttons statt iframe (Mobile-Browser unterstützen PDFs in iframes schlecht) */}
                              <div className="sm:hidden flex flex-col items-center justify-center py-12 px-6">
                                <FileText className="w-16 h-16 text-brand/50 mb-6" />
                                <p className="text-lg font-medium text-gray-900 dark:text-white mb-2 text-center">
                                  {previewInvoice.invoice_number}
                                </p>
                                <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 text-center">
                                  {formatInvoiceDate(previewInvoice.issue_date)} - {formatCurrency(previewInvoice.total)}
                                </p>
                                <div className="flex flex-col gap-3 w-full max-w-xs">
                                  <a
                                    href={pdfUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-brand hover:bg-brand-hover text-gray-900 font-medium rounded-lg transition-colors"
                                  >
                                    <ExternalLink className="w-5 h-5" />
                                    PDF anzeigen
                                  </a>
                                  <button
                                    onClick={() => handleDownloadInvoice(previewInvoice)}
                                    className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-200 dark:hover:bg-white/20 transition-colors"
                                  >
                                    <Download className="w-5 h-5" />
                                    PDF herunterladen
                                  </button>
                                </div>
                              </div>

                              {/* Desktop/Tablet: iframe */}
                              <iframe
                                src={pdfUrl}
                                className="hidden sm:block w-full h-full min-h-[60vh]"
                                title={`Rechnung ${previewInvoice.invoice_number}`}
                              />
                            </>
                          );
                        }
                        return (
                          <div className="flex flex-col items-center justify-center py-12">
                            <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
                            <p className="text-lg font-medium text-gray-900 dark:text-white mb-1">PDF nicht verfügbar</p>
                            <p className="text-gray-600 dark:text-gray-400 mb-4">
                              Die PDF-Vorschau konnte nicht geladen werden.
                            </p>
                            {previewInvoice.pdf_url && (
                              <button
                                onClick={() => handleDownloadInvoice(previewInvoice)}
                                className="flex items-center gap-2 px-4 py-2 bg-brand hover:bg-brand-hover text-gray-900 font-medium rounded-lg transition-colors"
                              >
                                <ExternalLink className="w-4 h-4" />
                                PDF öffnen
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Modal Footer - nur auf Desktop/Tablet */}
                    <div className="hidden sm:flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-white/10">
                      <button
                        onClick={() => setPreviewInvoice(null)}
                        className="px-4 py-2 rounded-lg text-sm font-medium
                          bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300
                          hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                      >
                        Schließen
                      </button>
                      {previewInvoice.pdf_url && (
                        <button
                          onClick={() => handleDownloadInvoice(previewInvoice)}
                          className="flex items-center gap-2 px-4 py-2 bg-brand hover:bg-brand-hover text-gray-900 font-medium rounded-lg transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Herunterladen
                        </button>
                      )}
                    </div>

                    {/* Mobile Footer - nur Schließen-Button */}
                    <div className="sm:hidden p-4 border-t border-gray-200 dark:border-white/10">
                      <button
                        onClick={() => setPreviewInvoice(null)}
                        className="w-full px-4 py-2.5 rounded-lg text-sm font-medium
                          bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300
                          hover:bg-gray-200 dark:hover:bg-white/10 transition-colors"
                      >
                        Schließen
                      </button>
                    </div>
                  </div>
                </div>,
                document.body
              )}
            </div>
          )}
        </div>
      </TWContainer>

      {/* Plan Change Dialog */}
      <PlanChangeDialog
        isOpen={planChangeDialogOpen}
        onClose={() => setPlanChangeDialogOpen(false)}
        targetPlanSlug={planChangeTargetSlug}
        onSuccess={handlePlanChangeSuccess}
        onDowngradeNeedsSelection={handleDowngradeNeedsSelection}
      />

      {/* Downgrade Resource Selector -- familien-spezifisch, strikt getrennt */}
      {resourceSelectorFamily === 'fcms' ? (
        <DowngradeResourceSelector
          isOpen={resourceSelectorOpen}
          onClose={() => setResourceSelectorOpen(false)}
          onSaved={handleResourceSelectionSaved}
          effectiveDate={downgradeEffectiveDate}
        />
      ) : (
        <BlogDowngradeSelector
          isOpen={resourceSelectorOpen}
          onClose={() => setResourceSelectorOpen(false)}
          onSaved={handleResourceSelectionSaved}
          effectiveDate={downgradeEffectiveDate}
        />
      )}

      {/* Cancel Success Modal */}
      {cancelSuccessInfo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setCancelSuccessInfo(null)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className="relative w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-status-success/10 text-status-success flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Kündigung erfolgreich</h3>
              <button
                onClick={() => setCancelSuccessInfo(null)}
                className="ml-auto p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-600 dark:text-gray-300 mb-2">
              Dein <strong className="text-gray-900 dark:text-white">"{cancelSuccessInfo.planName}"</strong>-Abonnement wurde erfolgreich gekündigt.
            </p>
            <p className="text-brand font-medium mb-6">
              Du hast noch Zugang bis zum <strong>{cancelSuccessInfo.accessUntil}</strong>.
            </p>
            <button
              onClick={() => setCancelSuccessInfo(null)}
              className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover text-gray-900 font-medium rounded-lg transition-colors"
            >
              Verstanden
            </button>
          </div>
        </div>
      )}
    </SidebarLayout>
  );
};

// ============================================
// HELPER COMPONENTS
// ============================================

type BadgeColor = 'gold' | 'teal' | 'green' | 'blue' | 'purple' | 'red' | 'yellow' | 'gray';

const badgeColorMap: Record<BadgeColor, Parameters<typeof TWBadge>[0]['color']> = {
  gold: 'brand',
  teal: 'teal',
  green: 'green',
  blue: 'blue',
  purple: 'purple',
  red: 'red',
  yellow: 'yellow',
  gray: 'gray',
};

function Badge({ color, icon, children }: { color: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <TWBadge color={badgeColorMap[color as BadgeColor] || 'gray'} icon={icon}>
      {children}
    </TWBadge>
  );
}

export default ProfilePage;
